/**
 * Manual de Oficina — personal de oficina (rol `oficina`, VNA-127).
 *
 * Gastos es la MISMA pantalla que usa el Chofer, así que sus secciones se reusan tal cual
 * (`chofer.sections`): copiarlas haría que una se actualice y la otra no.
 *
 * "Mi nómina" (la semana de horas) se documentó junto con su pantalla (VNA-129).
 */
import chofer from './chofer';

// Lo ÚNICO del flujo de Gastos que cambia para Oficina: su recibo va directo a Revisión
// Final (como el del socio), no al Ingeniero(a). Se reemplaza ese paso y nada más; el resto
// de las secciones del chofer describen la misma pantalla.
const PASO_CHOFER = 'Si todo está correcto, el recibo pasa a "Pendiente" y se envía al Ingeniero(a) del proyecto para su revisión.';
const PASO_OFICINA = 'Si todo está correcto, el recibo pasa a "Pendiente de Revisión" y va directo a Revisión Final, sin pasar por el Ingeniero(a) del proyecto.';
const seccionesGastos = chofer.sections.map(s => s.id !== 'enviar-revision' ? s : {
  ...s,
  blocks: s.blocks.map(b => b.type !== 'steps' ? b : { ...b, items: b.items.map(i => i === PASO_CHOFER ? PASO_OFICINA : i) }),
});

export default {
  roleKey: 'oficina',
  roleLabel: 'Oficina',
  icon: '🏢',
  subtitle: 'Sus recibos y sus solicitudes de vacaciones. Usted ve solo lo suyo.',
  sections: [
    ...seccionesGastos,
    {
      id: 'oficina-mi-nomina',
      group: 'Mi nómina',
      title: 'Cargar sus horas de la semana',
      blocks: [
        { type: 'paragraph', text: 'En el menú, abra Horas → Mi nómina. Verá la semana actual: una columna por día y una fila por cada proyecto y tarea en que trabajó. Con las flechas pasa a la semana anterior o siguiente.' },
        { type: 'steps', items: [
          'Pulse "Agregar fila", elija el proyecto y luego la tarea. En proyectos administrativos (por ejemplo, Villavicencio) se elige una cuenta GL en vez de una tarea.',
          'En cada día escriba las horas regulares (arriba) y las extra (abajo).',
          'Pulse "Guardar" cuando quiera: puede cargar un día a la vez y volver mañana. Lo guardado queda como Borrador.',
          'Cuando la semana esté completa, pulse "Someter semana". Cada proyecto de cada día se envía a Revisión Final como una tarjeta aparte.',
        ] },
        { type: 'subheading', text: 'Cómo funciona el envío y el reenvío' },
        { type: 'list', items: [
          'Cada proyecto de cada día es una tarjeta: si un día trabajó en dos proyectos, ese día tiene dos tarjetas.',
          'Una vez enviada, esa tarjeta queda bloqueada hasta que Revisión Final la apruebe o se la devuelva. No se puede cambiar ni volver a enviar por su cuenta.',
          'Si se la devuelven, solo esa tarjeta (ese proyecto, ese día) vuelve a quedar editable. Corríjala y pulse "Someter semana".',
          'Puede agregar otro proyecto a un día ya enviado: va como una tarjeta nueva.',
          '"Someter semana" envía solo lo que está en Borrador o Devuelto; lo ya enviado no se reenvía.',
        ] },
        { type: 'note', variant: 'info', text: 'Abajo verá el total de la semana. Para que se le acumulen vacaciones y enfermedad necesita completar sus horas del mes, así que cargue la semana entera, también el tiempo de oficina.' },
        { type: 'note', variant: 'warning', text: 'Si una fila tiene horas pero no tiene tarea, o si repite el mismo proyecto y tarea, al guardar verá un aviso con el nombre del proyecto. Un proyecto en un día ya enviado o aprobado no se puede cambiar.' },
        { type: 'note', variant: 'info', text: 'Si Revisión Final le devuelve una tarjeta, verá arriba el motivo y la nota del revisor, y ese día aparecerá como "Devuelto". Corríjala y pulse "Someter semana" de nuevo.' },
      ],
    },
    {
      id: 'oficina-pedir-vacaciones',
      group: 'Vacaciones',
      title: 'Pedir vacaciones o una ausencia',
      blocks: [
        { type: 'paragraph', text: 'En el menú, abra Vacaciones → Mis Solicitudes y pulse "Nueva Solicitud". El formulario ya viene con su nombre y con el proyecto administrativo de la empresa: no tiene que buscarse.' },
        { type: 'steps', items: [
          'Elija el "Tipo de ausencia".',
          'Indique las fechas "Desde" y "Hasta". El portal calcula las horas de los días laborables; puede ajustarlas en "Horas".',
          'Si quiere, escriba el motivo (es opcional).',
          'Pulse "Enviar solicitud".',
        ] },
        { type: 'note', variant: 'info', text: 'Su solicitud la aprueba Lisette Villavicencio. Arriba del formulario verá su saldo disponible; si pide más de lo que tiene, la solicitud se registra igual y se le avisa.' },
        { type: 'note', variant: 'warning', text: 'Si aparece "Tu usuario no está vinculado a un empleado", el portal todavía no sabe cuál es su ficha de empleado. Pídale a un administrador que la vincule en Usuarios; hasta entonces no podrá enviar la solicitud.' },
      ],
    },
    {
      id: 'oficina-mis-solicitudes',
      group: 'Vacaciones',
      title: 'Ver el estado de sus solicitudes',
      blocks: [
        { type: 'paragraph', text: 'Vacaciones → Mis Solicitudes muestra solo las suyas, con su estado. Pulse una para ver el detalle y quién la aprobó o por qué se denegó.' },
      ],
    },
  ],
};
